import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React from 'react';
import palette from '@ui/palette';
// eslint-disable-next-line import/no-named-default
import Typography from '@ui/Typography';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import BaseModal, { ModalProps as BaseModalProps } from './Modal';

export interface ModalV2Props
  extends Omit<BaseModalProps, 'onClick' | 'classes'>,
    HeaderProps,
    FooterProps {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  /**
   * True if should apply default container styling
   */
  withFooterContainer?: boolean;
  /**
   * True if backdrop should be hidden
   * Example: using prompt manager which enables this by default
   */
  hideBackdrop?: boolean;
  /**
   * True if CTA button in footer should be disabled
   */
  disabled?: boolean;
  classes?: BaseModalProps['classes'] & {
    footer?: string;
  };
}

const useStyles = makeStyles()(theme => ({
  modalBody: {
    backgroundColor: palette.gray_1000,
    borderRadius: theme.spacing(0.5),
  },
  header: {
    position: 'relative',
    display: 'grid',
    gridAutoFlow: 'column',
    gridTemplateColumns: '1fr',
    borderRadius: theme.spacing(0.5, 0.5, 0, 0),
    alignItems: 'center',
    padding: theme.spacing(2),
  },
  headerBanner: {
    backgroundColor: palette.gray_800,
  },
  title: {
    margin: theme.spacing(0.25, 0),
    justifySelf: 'center',
  },
  closeButton: {
    position: 'absolute',
    gridColumnStart: 2,
    justifySelf: 'end',
    marginLeft: 'auto',
    marginRight: theme.spacing(1.5),
    color: palette.gray_300,
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      backgroundOpacity: 0.14,
    },
  },
  backButton: {
    position: 'absolute',
    left: theme.spacing(1.5),
    color: palette.gray_300,
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(0.5),
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
      backgroundOpacity: 0.14,
    },
  },
  closeButtonWithoutTitle: {
    // 1.5 seemed slightly off so reduced to 1.25
    top: theme.spacing(1.25),
  },
  footerContainer: {
    borderTop: `1px solid ${palette.gray_700}`,
  },
  footerBtn: {
    padding: theme.spacing(1.5, 2),
  },
  footer: {
    display: 'grid',
  },
  footer_small_button: {
    gridTemplateColumns: 'repeat(3, 1fr)',
    '& > *': {
      gridColumnStart: 2,
    },
    padding: theme.spacing(3, 0, 3.75),
  },
  footer_full_width_button: {
    padding: theme.spacing(3, 5),
  },
}));

const useContentStyles = makeStyles<{ contentItems: number }>()(
  (theme, { contentItems }) => ({
    content: {
      display: 'grid',
      gridAutoFlow: 'column',
      /**
       * If more than 1 column in grid content, apply borders between them
       */
      '& > :first-of-type:not(:last-of-type)': {
        borderRight: `1px solid ${palette.gray_700}`,
      },
      gridTemplateColumns: `repeat(${contentItems}, 1fr)`,
    },
  })
);

interface HeaderProps extends Pick<BaseModalProps, 'onClose'> {
  title?: string;
  onBack?: () => void;
}

/**
 * @todo consider an alternative to making a conditional banner
 */
export const Header = ({ title, onClose, onBack }: HeaderProps) => {
  const { classes, cx } = useStyles();
  return (
    <Box className={cx(classes.header, title && classes.headerBanner)}>
      {onBack && (
        <Button
          variant="icon"
          aria-label="back"
          className={classes.backButton}
          onClick={onBack}
        >
          <Icon type="arrowLeft" />
        </Button>
      )}
      {title && (
        <Typography
          component="h1"
          variant="h5"
          bold
          color="tertiary"
          className={classes.title}
        >
          {title}
        </Typography>
      )}
      <Button
        variant="icon"
        aria-label={'close'}
        className={cx(
          classes.closeButton,
          !title && classes.closeButtonWithoutTitle
        )}
        onClick={onClose}
      >
        <Icon type="ex" />
      </Button>
    </Box>
  );
};

interface FooterProps {
  ctaLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  variant?: 'smallButton' | 'fullWidthButton';
}
export const Footer = (props: FooterProps) => {
  const {
    ctaLabel,
    loading,
    disabled,
    onClick,
    className,
    variant = 'smallButton',
  } = props;
  const { classes, cx } = useStyles();
  const isSmallButtonVariant = variant === 'smallButton';
  return (
    <Box
      className={cx(
        classes.footer,
        {
          [classes.footer_small_button]: isSmallButtonVariant,
          [classes.footer_full_width_button]: !isSmallButtonVariant,
        },
        className
      )}
    >
      <Button
        variant="primary"
        className={classes.footerBtn}
        type={'button'}
        onClick={() => {
          if (onClick) onClick();
        }}
        size={!isSmallButtonVariant ? undefined : 'large'}
        loading={loading}
        disabled={loading || disabled}
        fullWidth={!isSmallButtonVariant}
      >
        {ctaLabel}
      </Button>
    </Box>
  );
};

const Content = ({ children }: Pick<ModalV2Props, 'children'>) => {
  const contentItems = React.Children.count(children);
  const { classes } = useContentStyles({ contentItems });
  return <Box className={classes.content}>{children}</Box>;
};

const Modal = (props: ModalV2Props) => {
  const {
    open,
    header,
    footer,
    children,
    ctaLabel,
    title,
    onBack,
    loading,
    onClose,
    onClick,
    classes: incomingClasses,
    withFooterContainer = true,
    hideBackdrop = false,
    disabled,
    variant,
    ...rest
  } = props;

  const { classes, cx } = useStyles();
  return (
    <BaseModal
      open={open}
      includePaper={false}
      onClose={onClose}
      classes={{
        modalBody: cx(classes.modalBody, incomingClasses?.modalBody),
      }}
      disableGutters
      BackdropProps={{ invisible: hideBackdrop, ...rest.BackdropProps }}
      {...rest}
    >
      <Box>
        {header}
        {!header && <Header title={title} onClose={onClose} onBack={onBack} />}
      </Box>
      <Content>{children}</Content>
      <Box className={cx({ [classes.footerContainer]: withFooterContainer })}>
        {footer}
        {!footer && (
          <Footer
            disabled={disabled}
            ctaLabel={ctaLabel}
            loading={loading}
            onClick={onClick}
            className={incomingClasses?.footer}
            variant={variant}
          />
        )}
      </Box>
    </BaseModal>
  );
};

const ModalV2 = {
  Modal,
  Header,
  Footer,
};

export default ModalV2;
